/* eslint-disable @typescript-eslint/no-explicit-any */
import { Helmet } from "react-helmet";
import { observer } from "mobx-react";
import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "@/i18n/client";
import to from "await-to-js";
// -------------- api -------------------- //
import { CartData, BodyParam, ProductItemInfo } from "@/app/csr-src/apis/cart/cart.interface";
import { DEF_ADDRESS_BODY } from "@/common-components-src/api/address/address.interface";
import { fetchCartDetail, selectAll } from "@/app/csr-src/apis/cart/cart.api";
// -------------- 组件 -------------------- //
import { Button } from "@mui/material";
import CartList from "./cartList";
import CartOrder from "./cartOrder";
import TransactionStep from "@/app/csr-src/components/transactionStep";
import { DialogContent, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import JDILogin from "@/common-components-src/js/newlogin/index.newlogin";
import UKMaskLoading from "@/common-components-src/components/UKMaskLoading/index";
import GiftModal from "@/common-components-src/js/gift";
import sgmManager from "@/common-components-src/js/utils/sgmUtils";
import Recommend from "@/common-components-src/js/recommend/index";
import UKProductSelect from "@/common-components-src/components/UKProductSelect";
import UKDialog from "@/common-components-src/components/UKDialog/index";
// -------------- 图标 -------------------- //
import WarningIcon from "@/app/csr-src/assets/icons/WarningIcon.svg";
import EmptyBox from "@/app/csr-src/assets/icons/EmptyBox.svg";
// -------------- utils -------------------- //
import { addCartUtil } from "@/app/csr-src/utils/cartUtils";
import { getUserUUIDFromCookie } from "@/utils/userUtils";
import { sendPv, sendClickTrack, EXPOSURE_RECORD } from "@/tracks/25226";
import { EXPOSURE_RECORD as Recommend_EXPOSURE_RECORD, sendClickTrack as Recommend_sendClickTrack } from '@/tracks/25350'
// -------------- styles -------------------- //
import styles from "./index.module.scss";

const Cart = observer(() => {
  const { t } = useTranslation();
  const [productItems, setProductItems] = useState<{ info: ProductItemInfo }[] | null>(null); // 商品列表
  const [productSkuIds, setProductSkuIds] = useState(""); // 逗号分隔的字符串skuid

  const [cartData, setCartData] = useState<CartData | null>(null);
  const [isNotLogin, setIsNotLogin] = useState(true);
  const [isError, setIsError] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false); // 是否加载完成

  const location = useLocation();
  const navigate = useNavigate();

  const [isShowProdectModal, setIsShowProdectModal] = useState(false); // 是否显示选择商品类型弹窗
  const [isTypeLoaded, setIsTypeLoaded] = useState(false); // 选择商品是否加载完成
  const [cartSkuId, setCartSkuId] = useState(-1);
  const [dialogStyle, setDialogStyle] = useState({
    background: "transparent",
    boxShadow: "none",
  });
  useEffect(() => {
    if (isTypeLoaded) {
      setDialogStyle({
        background: "#fff",
        boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
      });
    } else {
      setDialogStyle({
        background: "transparent",
        boxShadow: "none",
      });
    }
  }, [isTypeLoaded]);

  useEffect(() => {
    loginStatus();
    addCart();
    sendPv.Shopcart_Main();
  }, []);

  const initLoadCartDetail = () => {
    UKMaskLoading.show();
    const requestData: BodyParam = {
      verticalTag: "cn_ybxt_b2c",
      area: DEF_ADDRESS_BODY.area,
      cartuuid: getUserUUIDFromCookie(),
      extendParamMap: {
        stock: true,
      },
    };
    loadCartDetails(requestData);
  };

  const refreshList = () => {
    initLoadCartDetail();
  };

  const loginStatus = async () => {
    const { isLoggedIn } = await JDILogin.getInstance().getUserInfo();
    setIsNotLogin(isLoggedIn);
  };

  const addCart = async () => {
    if (location?.state?.productList) {
      try {
        await addCartUtil(location?.state?.productList);
        initLoadCartDetail();

        const { pathname } = location;
        // 直接将 productList 设置为 null 防止进入结算后退再次渲染加车
        const newState = { ...location.state, productList: null };
        // 使用navigate函数更新location状态，同时保留路径等其他属性
        navigate(pathname, { state: newState, replace: true });
      } catch (error) {
        initLoadCartDetail();
      }
    } else {
      initLoadCartDetail();
    }
  };

  const handleClickLogin = () => {
    JDILogin.getInstance().openLoginDialog(() => { });
  };

  const loadCartDetails = async (bodyParam: BodyParam) => {
    const [err, result] = await to(fetchCartDetail(bodyParam));
    if (err) {
      setIsError(true);
      setCartData(null);

      sgmManager.errorLog({
        code: "api_cart_se_getCart",
        msg: "error",
      });
    } else {
      setIsError(!result);
      setCartData(result as CartData);
    }
    UKMaskLoading.close();
  };

  const otherLoadCartDetails = async (bodyParam: BodyParam) => {
    const [err, result] = await to(selectAll(bodyParam));
    if (err) {
      setIsError(true);
      setCartData(null);
    } else {
      setIsError(!result);
      setCartData(result as CartData);
    }
  };

  useEffect(() => {
    if (!cartData || !cartData.data) {
      return;
    }

    // 商品列表楼层
    const hierarchyRoot = cartData.hierarchy.root || [];
    const productItems: { info: ProductItemInfo }[] = [];
    const productSkuIds: string[] = [];

    function processHierarchyKey(key: string) {
      if (!cartData) {
        return;
      }
      const item = cartData.data[key];
      if (item && "type" in item && item.type) {
        const namespace = item.type.namespace;
        if (
          namespace === "core.trade-DM#cartView.product-M#productItem" ||
          namespace === "core.trade-DM#cartView.unusableProduct-M#defaultUnusableProductItem"
        ) {
          productItems.push(item as { info: ProductItemInfo });
          if (namespace === "core.trade-DM#cartView.product-M#productItem") {
            productSkuIds.push(
              (item as { info: ProductItemInfo })?.info?.["C-M#productItem&core"]?.skuId || "",
            );
          }
        }
      }
      const subKeys = cartData.hierarchy[key] || [];
      subKeys.forEach(processHierarchyKey);
    }

    hierarchyRoot.forEach(processHierarchyKey);
    setProductItems(productItems);

    if (productSkuIds.length > 0) {
      const firstTenItems = productSkuIds.slice(0, Math.min(productSkuIds.length, 10));
      setProductSkuIds(firstTenItems.join(","));
    }

    setIsLoaded(true);
  }, [cartData]);

  const recommendAddCart = (item: any) => {
    setCartSkuId(item?.skuId);
    setIsShowProdectModal(true);
    setIsTypeLoaded(false);
    setDialogStyle({
      background: "transparent",
      boxShadow: "none",
    });
  };

  const gotoCart = (targetSkuId: string) => {
    addCartUtil([{ skuId: targetSkuId, num: "1" }]).then(() => {
      const requestData: BodyParam = {
        verticalTag: "cn_ybxt_b2c",
        area: DEF_ADDRESS_BODY.area,
        cartuuid: getUserUUIDFromCookie(),
        extendParamMap: {
          stock: true,
        },
      };
      loadCartDetails(requestData);
    });
  };

  const handleClose = () => {
    setIsShowProdectModal(false);
  };

  return (
    <TransactionStep step={0} refresh={refreshList} cartData={cartData}>
      <GiftModal />
      <div
        className={styles["cart-container"]}
        onClick={(event) => {
          event.stopPropagation();
        }}
      >
        <Helmet>
          <title>My Cart | Joybuy</title>
          <meta
            name="description"
            content="Joybuy offers the best online shopping experience with fast shipping and delivery guarantee on all orders. Shop now for unbeatable deals on top-quality products."
          />
        </Helmet>
        {!isNotLogin && (
          <div
            className={styles["sign"]}
            exptag={"exp|" + EXPOSURE_RECORD.Shopcart_GuideLoginStrip_Expo}
            data-exptag-config='{"useClick": false, "stay_duration": 0.3, "repeated":false, "area_rate": 0.01}'
            data-exptag-json-param={{}}
          >
            <div className={styles["info-left"]}>
              <WarningIcon style={{ marginRight: "4px" }} />
              <span>{t("cart.login.signTips")}</span>
            </div>
            <span className={styles["info-right"]} onClick={() => {
              handleClickLogin();
              sendClickTrack.Shopcart_GuideLoginStrip_Click();
            }}>
              {t("cart.login.signUp")}
            </span>
          </div>
        )}
        <div className={styles["box-container"]}>
          <div className={styles["left"]}>
            {isError ? (
              <div
                className={styles["sign-box"]}
                exptag={"exp|" + EXPOSURE_RECORD.Shopcart_NullStatus_Expo}
                data-exptag-config='{"useClick": false, "stay_duration": 0.3, "repeated":false, "area_rate": 0.01}'
                data-exptag-json-param={JSON.stringify({ showType: "请求超时", is_login: isNotLogin ? "1" : "0" })}
              >
                <div className={styles["empty-box"]}>
                  <EmptyBox /> {t("cart.page.errorText")}
                </div>
                <Button className={styles["sign-in"]} onClick={() => {
                  refreshList();
                  sendClickTrack.Shopcart_NullStatusBtn_Click({ showType: "请求超时", is_login: isNotLogin ? "1" : "0" });
                }}>
                  {t("cart.page.refresh")}
                </Button>
              </div>
            ) : productItems && productItems.length > 0 ? (
              <CartList
                loadDataCheck={otherLoadCartDetails}
                cartData={cartData}
                setCartData={setCartData}
                refreshList={refreshList}
              />
            ) : productItems ? (
              <div className={styles["sign-box"]}
                exptag={"exp|" + EXPOSURE_RECORD.Shopcart_NullStatus_Expo}
                data-exptag-config='{"useClick": false, "stay_duration": 0.3, "repeated":false, "area_rate": 0.01}'
                data-exptag-json-param={JSON.stringify({ showType: "无商品", is_login: isNotLogin ? "1" : "0" })}
              >
                <div className={styles["empty-box"]}>
                  <EmptyBox /> {t("cart.page.emptyText")}
                </div>
                {isNotLogin && (
                  <Button
                    className={styles["sign-in"]}
                    onClick={() => {
                      window.location.href = "/";
                      sendClickTrack.Shopcart_NullStatusBtn_Click({ showType: "无商品", is_login: isNotLogin ? "1" : "0" });
                    }}
                  >
                    {t("cart.page.shopping")}
                  </Button>
                )}
              </div>
            ) : null}
          </div>
          <div className={styles["right"]}>
            <CartOrder
              cartData={cartData}
              loadDataCheck={otherLoadCartDetails}
              refreshList={refreshList}
            />
          </div>
        </div>
      </div>
      {isLoaded && (
        <Recommend 
          sceneCode="cart_page"
          mainSkus={productSkuIds} 
          addCartFn={recommendAddCart}
          trackParams={{
            RecomSku_Expo: Recommend_EXPOSURE_RECORD.Shopcart_RecomSku_Expo,
            RecomSku_Tab_Expo: Recommend_EXPOSURE_RECORD.Shopcart_RecomSku_Tab_Expo,
            RecomSku_Addcart_Expo: Recommend_EXPOSURE_RECORD.Shopcart_RecomSku_Addcart_Expo,
            productClick: Recommend_sendClickTrack.Shopcart_RecomSku,
            tabClick: Recommend_sendClickTrack.Shopcart_RecomSku_Tab,
            addCartClick: Recommend_sendClickTrack.Shopcart_RecomSku_Addcart,
          }} 
        />
      )}
      <UKDialog
        open={isShowProdectModal}
        onClose={handleClose}
        sx={{
          "& .MuiDialog-paper": {
            width: "720px", // 设置自定义宽度
            maxWidth: "none", // 取消最大宽度限制
            maxHeight: "640px",
            minHeight: "334px",
            ...dialogStyle,
          },
        }}
      >
        {isTypeLoaded && (
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={(theme) => ({
              position: "absolute",
              right: 8,
              top: 8,
              color: theme.palette.grey[500],
            })}
          >
            <CloseIcon />
          </IconButton>
        )}
        <DialogContent>
          <UKProductSelect
            skuId={cartSkuId}
            handleClose={handleClose}
            setIsLoaded={setIsTypeLoaded}
            updateItemData={gotoCart}
          />
        </DialogContent>
      </UKDialog>
    </TransactionStep>
  );
});

export default Cart;
